import { useLocation } from 'react-router';
import { useGlobalStore } from '../../stores/global-store';
import { selectView, type View } from './view-model';

/** Reactive view for rendering — re-derives on route or override change. */
export function useView(): View {
  const location = useLocation();
  const override = useGlobalStore((s) => s.viewOverride);
  return selectView(location.pathname, location.search, override);
}

/** Non-reactive snapshot for imperative logic (e.g. getResults). */
export function getView(): View {
  const override = useGlobalStore.getState().viewOverride;
  return selectView(window.location.pathname, window.location.search, override);
}

/** Clear the transient view override (if any) — called on every router
 *  navigation (see main.tsx) so an override never outlives the screen it was set
 *  for. No-op when there is no override. */
export function clearViewOverride(): void {
  if (useGlobalStore.getState().viewOverride) {
    useGlobalStore.getState().setViewOverride(null);
  }
}

/** Called on every router navigation (see main.tsx's router.subscribe). Normally
 *  clears the transient override, exactly like clearViewOverride — EXCEPT when
 *  viewOverrideSticky is true, meaning this navigation is the one a caller (e.g.
 *  clearSearchBar's forceStayOnResults) just set the override to survive: in that
 *  case, keep the override in place and consume the sticky flag instead of clearing,
 *  so the override still clears normally on the next, genuinely separate navigation. */
export function handleNavigationForViewOverride(): void {
  const store = useGlobalStore.getState();
  if (store.viewOverrideSticky) {
    store.setViewOverrideSticky(false);
    return;
  }
  clearViewOverride();
}
